import { faCentSign, faDollarSign } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	clone,
	currency,
	dateOf,
	dateTimeOf,
	Get,
	hasValue,
	holdInUtc,
	log,
	PushErrorNotification,
	styles,
	timeOf,
} from "@repo/helpers";
import { address, DocumentDefinition, KeyPairArray, SerializedFile } from "@repo/models";
import {
	AudioDisplay,
	AudioRecording,
	Checkbox,
	DateTimePicker,
	DateTimePickerProps,
	DocumentReview,
	DropDownList,
	FileUploader,
	ImageDisplay,
	SecretInput,
	Selector,
	Signature,
	TableDateDisplay,
	TablePropertyDisplay,
	TakePicture,
	TextArea,
	TextBox,
	ToggleSlider,
} from "@repo/ui";
import { useDebouncer } from "@tanstack/react-pacer";
import { addDays, addMonths, addYears } from "date-fns";
import { FocusEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isMobile } from "react-device-detect";

import { AddressAutocomplete } from "../components/addressAutocomplete";
import BlinkId from "../components/blinkId";
import CurrentLocationFinder from "../components/currentLocationFinder";
import DocumentTypeUpload from "../components/documentTypeUpload";
import DynamicButton from "../components/dynamicButton";
import { EntryTextWithValidation } from "../components/entryTextWithValidation";
import GoogleMapCapture from "../components/GoogleMapCapture";
import {
	ComponentProvider,
	useComponentContext,
	useRerenderWithConsumer,
	useStableComponentContext,
} from "../contexts/componentContext";
import { usePendingEditsContext } from "../contexts/pendingEdits";
import { useValidationContext } from "../contexts/validation";
import { traverseProperty } from "../helpers/dynamicProperties";
import { MemoizeWithSectionsByReference } from "../helpers/memo";
import { DateRange } from "../helpers/validation";
import { component } from "../models/component";
import { componentType } from "../models/enums";
import CustomComponent from "./customComponent";
import style from "./style.module.scss";
import Template, { getTemplateValue } from "./template";
import { Props } from "./types";

const SIZES = new Map([
	["smaller", style.fontSizeSmaller],
	["small", style.fontSizeSmall],
	["large", style.fontSizeLarge],
	["larger", style.fontSizeLarger],
]);

const COLORS = new Map([
	["success", style.success],
	["warning", style.warning],
	["error", style.error],
]);

const WEIGHTS = new Map([
	["bold", style.bold],
	["semibold", style.semiBold],
]);

const DynamicComponent = MemoizeWithSectionsByReference(DynamicComponentInternal);
function DynamicComponentInternal(props: Props) {
	const { component, parentPropertyName, readOnly: propsReadOnly, isConfirmationProperty } = props;
	// Stable callbacks (see StableComponentContextType): this component re-renders when its own
	// props/state change, not whenever the consumer re-renders with new callback functions.
	const {
		valueChanged,
		valuesChanged,
		setCopiedFrom,
		deleteUploadedFile,
		getUploadedFile,
		hasComponentAddon,
		customFunctions,
		locationTracker,
	} = useStableComponentContext();
	const {
		label: componentLabel = "",
		component: baseComponent = {} as component,
		propertyName,
		propertyValue,
		extensionPropertyName,
		styleOptions,
		extensionPropertyValue,
		extendedProperties,
		components,
		type,
		visible,
		disabled,
		readOnly: componentReadOnly,
		optional,
		optionalLoaded: optionsLoaded,
		url,
		linkName,
		loadDataIsCustom,
		loadHandledInternal,
		confirm,
	} = component;
	const {
		inline,
		size,
		selectIfSingle,
		autoSelect,
		multiSelect,
		useAllDisplay,
		useRootPropertyName,
		mask,
		min,
		max,
		minLength,
		maxLength,
		prefix,
		hasUrl,
		explicitOptions,
		apiKey,
	} = extendedProperties;
	const {
		max: baseComponentMax,
		min: baseComponentMin,
		value: baseComponentValue,
		allowDecimals,
		copiedFromName,
		copiedFromProperty,
	} = baseComponent;

	// Fields whose value is an object (addresses, file lists, arrays, document definitions, ...) render
	// straight from that object, and some consumers update those objects in place. Keep re-rendering
	// them on every consumer render, as all fields did before; primitive-valued fields don't need to.
	useRerenderWithConsumer(
		isObjectLike(propertyValue) ||
			isObjectLike(extensionPropertyValue) ||
			(isObjectLike(optional) && !Array.isArray(optional)),
	);

	//Gets dropdown options for dropdown selector.tsx
	const preLoadedOptions = useMemo(() => {
		if (!optional) return null;
		if (typeof optional === "string") return null;

		const options = !Array.isArray(optional) ? Object.keys(optional).map((key) => optional[key]) : optional;
		if (loadDataIsCustom) return clone(optional);
		return new KeyPairArray(options.filter(hasValue));
	}, [optional]);
	const readOnly = useMemo(() => propsReadOnly || componentReadOnly || false, [propsReadOnly, componentReadOnly]);
	const fullReadOnly = useMemo(() => propsReadOnly || false, [propsReadOnly]);
	const [inputValue, setInputValue] = useState(propertyValue);
	const [extensionValue, setExtensionValue] = useState(extensionPropertyValue);
	const [options, setOptions] = useState(preLoadedOptions);
	const [loadError, setLoadError] = useState<string>();
	const [hasFocused, setHasFocused] = useState(false);

	const [confirmationProperty, setConfirmationProperty] = useState(null);
	useEffect(() => {
		if (confirm && propertyValue && !inputValue) {
			setConfirmationProperty(propertyValue);
		}
	}, [confirm, propertyValue]);

	const { hideValidation, validationResults, validateOnFocus, validateComponentIf } = useValidationContext();
	const showValidationByRule = useMemo(
		() => !hideValidation || (validateOnFocus && hasFocused),
		[hideValidation, validateOnFocus, hasFocused],
	);
	const showValidation = useMemo(
		() => validateComponentIf?.(component, showValidationByRule) ?? showValidationByRule,
		[validateComponentIf, component, showValidationByRule],
	);
	const validationError = useMemo(() => {
		if (!showValidation) return null;
		if (confirm && !isConfirmationProperty && propertyValue !== confirmationProperty) return "Values do not match";

		const error = validationResults?.getError(component);
		if (error) return error;
	}, [showValidation, validationResults, component, confirmationProperty]);

	const onBlur = useCallback(() => {
		setHasFocused(true);
	}, [setHasFocused]);

	const componentStyles = useMemo(() => {
		if (!styleOptions) return null;
		return styles(SIZES.get(styleOptions.size), COLORS.get(styleOptions.color), WEIGHTS.get(styleOptions.weight));
	}, [styleOptions]);

	/**
	 * Returns props that generally used by all InputComponent components
	 */
	const defaultProps = useMemo(
		() => ({
			label: componentLabel,
			id: propertyName,
			value: inputValue,
			validationError,
			readOnly,
			disabled: readOnly || disabled,
			includeError: true,
			onUnfocused: onBlur,
			inline: inline ?? undefined,
			/**Chrome seems to ignore "off", but "noop" seems to do the trick */
			autocomplete: "noop",
			loadError,
			componentStyles,
		}),
		[
			inputValue,
			componentLabel,
			propertyName,
			disabled,
			inline,
			onBlur,
			validationError,
			readOnly,
			loadError,
			componentStyles,
		],
	);

	const requested = useRef(false);

	useEffect(() => {
		if (url) {
			if (visible) getAsyncValues();
			else requested.current = false;
		} else setOptions(preLoadedOptions);
	}, [url, preLoadedOptions]);

	useEffect(() => {
		if (visible && !requested.current && url) getAsyncValues();
	}, [visible]);

	// inputValue catches up with propertyValue in this effect, so in the render where propertyValue changes it
	// still holds the previous value (the next render has the new one)
	const syncedPropertyValue = useRef(propertyValue);
	const inputValueBehind =
		!Object.is(propertyValue, syncedPropertyValue.current) && !Object.is(inputValue, propertyValue);
	/** Values this field sent through the debounce that have not come back as propertyValue yet, oldest first */
	const sentValues = useRef<unknown[]>([]);
	useEffect(() => {
		syncedPropertyValue.current = propertyValue;
		// A value this field sent can come back after the user typed more, when the consumer saves it only after
		// awaiting business logic (telemarketing). Showing it would drop what was typed since ("sam@" became "sam"
		// and the next keystroke saved "samt"), so keep the input while a newer edit is sent or waiting on the
		// debounce: that edit lands next. Any other value (another record, Cancel, a lookup, business logic that
		// changed the value) still replaces the input.
		const sent = sentValues.current;
		const echo = sent.findIndex((value) => Object.is(value, propertyValue));
		if (echo < 0) sent.length = 0;
		else {
			sent.splice(0, echo + 1);
			const typedSince = sent.length > 0 || updateDebouncer.store.state.isPending;
			if (typedSince && !Object.is(latestValues.current.inputValue, propertyValue)) return;
		}
		setInputValue(propertyValue);
	}, [propertyValue]);
	useEffect(() => {
		setExtensionValue(extensionPropertyValue);
	}, [extensionPropertyValue]);

	/**
	 * Loads values from url, and updates in state
	 */
	const getAsyncValues = async function () {
		requested.current = true;
		setOptions(null);
		setLoadError(null);
		if (!url) {
			requested.current = false;
			return;
		}
		//Some components handle loading of data theirselves, and url is only updated to force
		//a react component update
		if (loadHandledInternal) return;

		try {
			const response = await Get(url, null);
			updateAsyncValues(response);
		} catch (ex) {
			const message = ex?.message?.message ?? ex?.message ?? (ex as string);
			setLoadError(message);
			log(ex, "error");
			updateAsyncValues([]);
		}

		function updateAsyncValues(response: any) {
			setOptions(loadDataIsCustom ? clone(response) : new KeyPairArray(response));
		}
	};

	/**
	 * Debounces the valueChanged function to prevent excessive re-renders
	 */
	const updateDebouncer = useDebouncer(
		(value, id?: string) => {
			sentValues.current.push(value);
			return valueChanged(value, id || propertyName, parentPropertyName);
		},
		{ wait: 250 },
	);

	// Lets the consumer flush or drop this field's edit while it waits on the debounce (contexts/pendingEdits).
	// Dropping also shows the data value again: an edit that never lands never changes propertyValue, so the
	// [propertyValue] sync effect would not reset the typed value.
	const pendingEdits = usePendingEditsContext();
	const latestValues = useRef({ inputValue, propertyValue, extensionValue, extensionPropertyValue });
	latestValues.current = { inputValue, propertyValue, extensionValue, extensionPropertyValue };
	useEffect(
		() =>
			pendingEdits.register({
				flush: () => updateDebouncer.flush(),
				discard: () => {
					updateDebouncer.cancel();
					sentValues.current.length = 0;
					const values = latestValues.current;
					if (values.inputValue !== values.propertyValue) setInputValue(values.propertyValue);
					if (values.extensionValue !== values.extensionPropertyValue)
						setExtensionValue(values.extensionPropertyValue);
				},
			}),
		[pendingEdits],
	);

	const componentValueChanged = useCallback(
		async function (value: any, id?: string) {
			//switching the order of these might make ctrl z work, but it breaks dates
			setInputValue(value);
			updateDebouncer.maybeExecute(value, id);
		},
		[propertyName, parentPropertyName, valueChanged],
	);

	const fileUploadChange = useCallback(
		async (id: string, files: SerializedFile[], removeIndex?: number) => {
			const currentFiles = propertyValue.values ?? [];
			const newFiles = [...new Set([...currentFiles, ...files])];
			if (hasValue(removeIndex)) newFiles.splice(removeIndex, 1);
			await valueChanged(newFiles, id || propertyName);
		},
		[valueChanged, propertyName, propertyValue],
	);

	const dropdownChanged = useCallback(
		async (value: string | number, id?: string, dontEdit?: boolean) => {
			// The selector's automatic corrections (pick the only option, clear a value missing from the list) use
			// the value and options it was rendered with. In the render where propertyValue changes the value is
			// still the previous one while the options are already new, so a correction from that render works on
			// a stale pair: it overwrote a new value that is valid, e.g. dependent dropdowns cleared or re-picked
			// right after Cancel. Skip it when there is a new value: the next render passes the synced value, so the
			// selector runs its corrections again on a consistent pair. A new empty value (a cascade cleared this
			// field) still gets the correction, as before. Corrections are dontEdit, except a clear whose id the
			// selector's substring check finds inside another id (5 in [2,15]): a clear from that render is a
			// correction too (skipped, or written without marking the form edited).
			const correction = inputValueBehind && !hasValue(value) ? true : dontEdit;
			if (correction && inputValueBehind && hasValue(propertyValue)) return;
			const propName = propertyName;
			const oldValue = inputValue;
			//This function gets called a lot just thru ui updates.
			//Bounce early if value hasn't actually changed
			if (oldValue === value) return;

			if (!oldValue && !value) {
				//If default value is 0 for field, blanked out is "undefined", so this will always show as changed
				//without this code
				const zeroValue = options && options.get(0);
				if (!zeroValue) return;
			}

			let newName = options?.getValue(value);
			if (Array.isArray(value)) newName = value.map((v) => options?.getValue(v)).join(",");
			else if (!newName) newName = "";
			//Default dropdowns update both propName + "Id" and propName + "Name".
			//useRootPropertyName dropdowns only save the selected id, to propName itself. It's null instead of
			//undefined when cleared, so the cleared value still gets sent to the server.
			const idProperty = useRootPropertyName
				? { id: propName, value: value ?? null }
				: { id: propName + "Id", value };
			const nameProperty = options && !useRootPropertyName ? { id: propName + "Name", value: newName } : null;
			if (valuesChanged) {
				//If multi-update enabled, use this to update id and name at same time
				setInputValue(idProperty.value);
				const properties = [idProperty];
				if (nameProperty) properties.push(nameProperty);
				await valuesChanged(properties, parentPropertyName, correction);
			} else {
				await componentValueChanged(idProperty.value, idProperty.id);
				if (nameProperty) await valueChanged(nameProperty.value, nameProperty.id);
			}
		},
		[
			valueChanged,
			valuesChanged,
			options,
			inputValue,
			inputValueBehind,
			propertyValue,
			propertyName,
			parentPropertyName,
			useRootPropertyName,
		],
	);

	const extensionChanged = useCallback(
		async (value: string) => {
			await valueChanged(value.replace(/[^\d]/, ""), extensionPropertyName);
			setExtensionValue(value);
		},
		[valueChanged, setExtensionValue, extensionPropertyName],
	);

	const phoneChanged = useCallback(
		(value: string) => {
			componentValueChanged(value?.replace("+1 ", "").trim());
			//below doesn't quite work, as our validation actually checks with formatting
			//this.updateTraversedProperty(id, value.replace(/\D/g, ''));
		},
		[componentValueChanged],
	);

	const numberChanged = useCallback(
		(value: string) => {
			if (value === "-") {
				componentValueChanged(value);
			} else if (value === ".") {
				componentValueChanged("0.");
			} else if (value.startsWith(".")) {
				componentValueChanged(`0${value}`);
			} else {
				const currencyRegex = /^-?\d+\.?\d{0,5}/;
				const match = value.match(currencyRegex);
				componentValueChanged(match ? match[0] : "");
			}
		},
		[componentValueChanged],
	);

	/**
	 * If a currency component has been changed, this removes any commas
	 */
	const currencyUnfocused = useCallback(
		(event: FocusEvent<HTMLInputElement>) => {
			if (event.target.value?.includes(",")) componentValueChanged(event.target.value.replaceAll(",", ""));
			onBlur();
		},
		[onBlur, componentValueChanged],
	);

	const dynamicComponent = getComponent();
	const confirmationComponent = getConfirmationComponent();
	function getConfirmationComponent() {
		if (!confirm || isConfirmationProperty) return null;
		const confirmationComponent = {
			...component,
			propertyValue: confirmationProperty,
			validationError: validationError,
		};
		return (
			<ComponentProvider valueChanged={async (value: any) => setConfirmationProperty(value)}>
				<DynamicComponent {...props} isConfirmationProperty component={confirmationComponent} />
			</ComponentProvider>
		);
	}

	if (!visible) return null;

	const dynamicComponentGroup = confirmationComponent ? (
		<>
			{dynamicComponent}
			{confirmationComponent}
		</>
	) : (
		dynamicComponent
	);

	if (hasComponentAddon) {
		return (
			<>
				<ComponentAddon propertyName={propertyName} />
				{dynamicComponentGroup}
			</>
		);
	}
	return dynamicComponentGroup;

	function getComponent() {
		switch (type) {
			case componentType.entryText:
				return getEntryField();
			case componentType.entryTextArea:
				return getEntryTextArea();
			case componentType.entryTextAutocomplete:
				return getEntryTextAutocomplete();
			case componentType.entryTextWithLookup:
				return getEntryTextWithLookup();
			case componentType.entryTextWithValidation:
				return getEntryTextWithValidation();
			case componentType.entryNumber:
				return getEntryNumber();
			case componentType.entryDate:
				return getEntryDate();
			case componentType.entryTime:
				return getEntryTime();
			case componentType.entryCurrency:
				return getEntryCurrency();
			case componentType.entryPhone:
				return getEntryPhone();
			case componentType.entryEmailAddress:
				return getEntryEmail();
			case componentType.entryAddress:
				return getAddressComponent();
			case componentType.entryAddressAutocomplete:
				return getAddressAutocomplete();
			case componentType.entryZip:
				return getEntryZip();
			case componentType.entrySsn:
				return renderEntrySsn();
			case componentType.entrySecret:
				return getSecretInput();
			case componentType.entryCheckbox:
				return getCheckboxComponent();
			case componentType.entryLabeledCheckbox:
				return getCheckboxComponent(true);
			case componentType.entryBoolean:
				return getBooleanComponent();
			case componentType.entryNullableBoolean:
				return getNullableBooleanComponent();
			case componentType.entryDropdown:
				return getEntryDropdown();
			case componentType.radioButtonList:
			case componentType.panelSelector:
				return getEntrySelector();
			case componentType.dateSelector:
				return getDateSelection();
			case componentType.monthSelector:
				return getMonthSelection();
			case componentType.photoCapture:
				return getPhotoCapture();
			case componentType.selfiePhotoCapture:
				return getPhotoCapture(true);
			case componentType.mobilePhotoCapture:
				return getPhotoCapture(false, isMobile);
			case componentType.mobileOnlyPhotoCapture:
				return getPhotoCapture(false, isMobile, true);
			case componentType.audioRecording:
				return getAudioRecording();
			case componentType.blinkId:
				return getBlinkId();
			case componentType.fileUpload:
				return getEntryFileUpload();
			case componentType.documentTypeUpload:
				return getDocumentTypeUpload();
			case componentType.documentReview:
				return getDocumentReview();
			case componentType.entrySignature:
				return getEntrySignature();
			case componentType.entrySignDocument:
				return getEntryDocument();
			case componentType.googleMapCapture:
				return getGoogleMapCapture();
			case componentType.currentLocationFinder:
				return getCurrentLocationFinder();
			case componentType.button:
				return getButton();
			case componentType.template:
				return getTemplate();
			case componentType.bulletedTemplate:
				return getBulletedTemplate();
			case componentType.htmlTemplate:
				return getHtmlTemplate();
			case componentType.text:
				return getText();
			case componentType.propertyDisplay:
				return getPropertyDisplay();
			case componentType.propertyDateDisplay:
				return getPropertyDateDisplay();
			case componentType.propertyDateOnlyDisplay:
				return getPropertyDateOnlyDisplay();
			case componentType.propertyCurrencyDisplay:
				return getPropertyCurrencyDisplay();
			case componentType.propertyTimeOnlyDisplay:
				return getPropertyTimeOnlyDisplay();
			case componentType.propertyPhotoDisplay:
				return getPropertyPhotoDisplay();
			case componentType.title:
				return getTitle();
			case componentType.tablePropertyDisplay:
				return getTablePropertyDisplay();
			case componentType.tableDateDisplay:
				return getTableDateDisplay();
			case componentType.tableDateOnlyDisplay:
				return getTableDateDisplay(true);
			case componentType.addressDisplay:
				return getDisplayAddressComponent();
			case componentType.imageDisplay:
				return getImageDisplay();
			case componentType.audioDisplay:
				return getAudioDisplay();
			default:
				return (
					<CustomComponent
						{...props}
						valueChanged={valueChanged}
						valuesChanged={valuesChanged}
						customFunctions={customFunctions}
						componentValueChanged={componentValueChanged}
						inputValue={inputValue}
						extensionValue={extensionValue}
						options={options as KeyPairArray<any>}
						optionsLoaded={optionsLoaded}
						readOnly={readOnly || disabled}
						fullReadOnly={fullReadOnly}
						validationError={validationError}
						loadError={loadError}
						onBlur={onBlur}
						validationResults={validationResults}
					/>
				);
		}
	}

	function getEntryField() {
		return (
			<TextBox
				{...defaultProps}
				min={minLength}
				max={maxLength}
				prefix={prefix}
				changeHandler={componentValueChanged}
			/>
		);
	}

	function getEntryTextArea() {
		return (
			<TextArea
				{...defaultProps}
				rows={3}
				minLength={minLength}
				maxLength={maxLength}
				changeHandler={componentValueChanged}
			/>
		);
	}

	function getEntryTextAutocomplete() {
		const list = options && options.map((o) => o.value);
		return (
			<TextBox
				{...defaultProps}
				min={minLength}
				max={maxLength}
				prefix={prefix}
				changeHandler={componentValueChanged}
				autocompleteList={list}
			/>
		);
	}

	function getEntryTextWithLookup() {
		const childComponents = useMemo(
			() =>
				components?.map((c) => (
					<DynamicComponent
						{...props}
						component={c}
						key={c.id + c.type}
						parentPropertyName={propertyName}
						readOnly={c.readOnly}
						fullReadOnly
					/>
				)),
			[props, propertyName, fullReadOnly],
		);

		return <div className={style.entryTextWithLookupContainer}>{childComponents}</div>;
	}

	function getEntryTextWithValidation() {
		return (
			<EntryTextWithValidation
				{...defaultProps}
				mappedComponent={component}
				min={minLength}
				max={maxLength}
				prefix={prefix}
				componentValueChanged={componentValueChanged}
				valueChanged={valueChanged}
				customFunctions={customFunctions}
			/>
		);
	}

	function getEntryNumber() {
		const HelperText = useCallback(() => {
			if (!baseComponentValue) return null;
			const value = traverseProperty<number>(baseComponentValue, props);
			if (!hasValue(value)) return null;
			return <p className={style.inputText}>{value}</p>;
		}, [baseComponentValue]);

		return (
			<div className={style.inputFlexbox}>
				<div className={style.inputWithText}>
					<TextBox
						{...defaultProps}
						value={inputValue != null ? inputValue : undefined}
						min={baseComponentMin as any}
						max={baseComponentMax as any}
						maxlength={baseComponentMax?.length}
						changeHandler={numberChanged}
						type="number"
						inputMode="numeric"
					/>
				</div>
				<HelperText />
			</div>
		);
	}

	function getEntryDate() {
		function dateOfBirth(minYears: number | undefined, minDate: Date, maxYears: number | undefined, maxDate: Date) {
			if (minYears && maxYears) {
				maxDate.setFullYear(maxDate.getFullYear() - minYears);
				minDate.setFullYear(minDate.getFullYear() - maxYears);
				return { minDate, maxDate };
			} else if (minYears) {
				maxDate.setFullYear(maxDate.getFullYear() - minYears);
				return { maxDate };
			} else if (maxYears) {
				minDate.setFullYear(minDate.getFullYear() - maxYears);
				maxDate.setDate(new Date().getDate());
				return { minDate, maxDate };
			} else {
				maxDate.setDate(new Date().getDate());
				return {
					maxDate,
				};
			}
		}

		type DateType = "year" | "month" | "day";

		function addType(type: DateType, num: number | undefined, date: Date): number {
			switch (type) {
				case "year":
					return addYears(date, num).getDate();
				case "month":
					return addMonths(date, num).getMonth();
				case "day":
					return addDays(date, num).getDate();
				default:
					throw new Error("Invalid sub type");
			}
		}

		function offset(
			type: DateType,
			min: number | undefined,
			max: number | undefined,
			minDate: Date,
			maxDate: Date,
		) {
			if (min && max) {
				maxDate.setDate(addType(type, max, maxDate));
				minDate.setDate(addType(type, min, minDate));
				return { minDate, maxDate };
			} else if (min) {
				maxDate.setDate(addType(type, min, maxDate));
				return { maxDate };
			} else if (max) {
				minDate.setDate(addType(type, max, minDate));
				return { minDate };
			} else {
				return {};
			}
		}

		const options = useMemo(() => {
			const minDate = new Date();
			const maxDate = new Date();
			switch (baseComponentValue) {
				case DateRange.adultDoB.toString():
					maxDate.setFullYear(maxDate.getFullYear() - 18);
					return { maxDate };
				case DateRange.past.toString():
					maxDate.setDate(maxDate.getDate() - 1);
					return { maxDate };
				case DateRange.current.toString():
					minDate.setHours(0, 0, 0, 0);
					return { minDate };
				case DateRange.future.toString():
					minDate.setDate(minDate.getDate() + 1);
					return { minDate };
				case DateRange.dateOfBirth.toString():
					return dateOfBirth(
						component.extendedProperties.min,
						minDate,
						component.extendedProperties.max,
						maxDate,
					);
				case DateRange.year.toString():
					return offset(
						"year",
						component.extendedProperties.min,
						component.extendedProperties.max,
						minDate,
						maxDate,
					);
				case DateRange.month.toString():
					return offset(
						"month",
						component.extendedProperties.min,
						component.extendedProperties.max,
						minDate,
						maxDate,
					);
				case DateRange.day.toString():
					return offset(
						"day",
						component.extendedProperties.min,
						component.extendedProperties.max,
						minDate,
						maxDate,
					);
				default:
					return {};
			}
		}, [baseComponentValue]);

		return (
			<DateTimePicker {...defaultProps} changeHandler={componentValueChanged} {...options} allowInput dateOnly />
		);
	}

	function getEntryTime() {
		const { value, ...rest } = defaultProps;

		return <TextBox {...rest} value={value} type="time" changeHandler={componentValueChanged} step="60" />;
	}

	function getEntryCurrency() {
		const { value, ...rest } = defaultProps;

		const currencyIcon = useMemo(() => (prefix === "Cents" ? faCentSign : faDollarSign), [baseComponentValue]);

		const maskOptions = useMemo(() => {
			return {
				mask: mask ?? Number,
				scale: allowDecimals ?? 2,
				min: min,
				max: max,
				thousandsSeparator: ",",
				radix: ".",
				padFractionalZeros: true,
				normalizeZeros: true,
				autofix: false,
			};
		}, [mask, min, max, allowDecimals]);

		return (
			<TextBox
				{...rest}
				value={value?.toString() ?? ""}
				maskOptions={maskOptions}
				leftAddon={<FontAwesomeIcon icon={currencyIcon} />}
				type="text"
				inputMode="decimal"
				changeHandler={componentValueChanged}
				onUnfocused={(event) => currencyUnfocused(event)}
			/>
		);
	}

	function getEntryPhone() {
		return (
			<div>
				<div className={styles(extensionPropertyName && style.phoneNumberContainer)}>
					<TextBox
						{...defaultProps}
						maskOptions={{
							mask: [
								{
									mask: "(000) 000-0000",
								},
								{
									mask: "+1 (000) 000-0000",
								},
							],
						}}
						changeHandler={phoneChanged}
						placeholder={componentLabel}
						type="tel"
					/>
				</div>
				{extensionPropertyName && (
					<div className={style.extensionContainer}>
						<TextBox
							value={extensionValue}
							label={"Extension"}
							id={extensionPropertyName}
							placeholder={"Extension"}
							readOnly={readOnly}
							disabled={disabled}
							includeError
							changeHandler={extensionChanged}
							type="tel"
						/>
					</div>
				)}
			</div>
		);
	}

	function getEntryEmail() {
		return <TextBox {...defaultProps} changeHandler={componentValueChanged} type="email" inputMode="email" />;
	}

	function getAddressComponent() {
		const address = useMemo(() => inputValue as address, [inputValue]);

		const CopyButton = useCallback(() => {
			const hasCopiedFrom = hasValue(copiedFromProperty) && hasValue(copiedFromName);
			const index = Number(parentPropertyName.charAt(parentPropertyName.length - 2));
			const isFirstIteratedSection = (isNaN(index) ? 0 : index) < 1;
			if (!hasCopiedFrom || !isFirstIteratedSection) return null;
			return (
				<ToggleSlider
					id={`${propertyName}.matches`}
					value={address?.matches}
					disabled={readOnly || disabled}
					rounded
					label={`Same as ${copiedFromName}`}
					changeHandler={() => setCopiedFrom(component)}
				/>
			);
		}, [
			propertyName,
			address?.matches,
			readOnly,
			disabled,
			component,
			copiedFromProperty,
			copiedFromName,
			parentPropertyName,
		]);

		const addressComponents = useMemo(() => {
			const fullProps = {
				...props,
				readOnly: fullReadOnly,
				parentPropertyName: propertyName,
			};
			return components.map((c) => (
				<DynamicComponent
					{...fullProps}
					component={c}
					key={c.propertyName}
					parentPropertyName={propertyName}
					readOnly={c.readOnly || fullReadOnly}
					fullReadOnly
				/>
			));
		}, [props, fullReadOnly, propertyName, components]);

		if (!address) return null;

		return (
			<div id={propertyName} data-subsection={propertyName} className={style.address}>
				<h4>{componentLabel}</h4>
				<CopyButton />
				{addressComponents}
			</div>
		);
	}

	function getAddressAutocomplete() {
		return (
			<AddressAutocomplete
				{...defaultProps}
				id={linkName}
				defaultZip={extensionPropertyValue}
				changeHandler={componentValueChanged}
			/>
		);
	}

	function getEntryZip() {
		return (
			<TextBox
				{...defaultProps}
				changeHandler={componentValueChanged}
				maxlength={5}
				placeholder={"Zip Code"}
				type="number"
				inputMode="numeric"
			/>
		);
	}

	function renderEntrySsn() {
		return (
			<SecretInput
				{...defaultProps}
				mask={"000-00-0000"}
				inputMode="numeric"
				changeHandler={componentValueChanged}
			/>
		);
	}

	function getSecretInput() {
		return <SecretInput {...defaultProps} changeHandler={componentValueChanged} />;
	}

	function getCheckboxComponent(showTitle = false) {
		const label = getTemplateValue(props);
		return (
			<Checkbox
				{...defaultProps}
				changeHandler={componentValueChanged}
				label={label}
				title={showTitle && componentLabel}
			/>
		);
	}

	function getBooleanComponent() {
		return <ToggleSlider {...defaultProps} changeHandler={componentValueChanged} rounded hideAddon />;
	}

	function getNullableBooleanComponent() {
		const values = (options && options.data) || [
			{ id: false, value: "No" },
			{ id: true, value: "Yes" },
		];
		return (
			<Selector
				{...defaultProps}
				values={values}
				id={linkName}
				readOnly={readOnly}
				loaded={!url || options !== null}
				size={size}
				clearable={selectIfSingle}
				changeHandler={(value) => componentValueChanged(value)}
				renderAsPanels={false}
			/>
		);
	}

	function getEntryDropdown() {
		const placeholder = useMemo(() => (hasUrl ? (readOnly ? "" : "Select...") : null), [hasUrl, readOnly]);

		const dropdownValues = useMemo(() => (preLoadedOptions ?? options)?.data ?? [], [preLoadedOptions, options]);

		const loaded = useMemo(
			() => (hasUrl ? !url || hasValue(options) : (optionsLoaded ?? hasValue(options))),
			[hasUrl, url, options, optionsLoaded],
		);

		const childComponents = useMemo(
			() => (
				<>
					{components?.map((c) => (
						<DynamicComponent
							{...props}
							component={c}
							key={c.propertyName}
							parentPropertyName={propertyName}
							readOnly={c.readOnly || fullReadOnly}
							fullReadOnly
						/>
					))}
				</>
			),
			[props, propertyName, fullReadOnly],
		);

		return (
			<>
				<DropDownList
					{...defaultProps}
					values={dropdownValues}
					id={linkName}
					changeHandler={dropdownChanged}
					clearable
					selectIfSingle={selectIfSingle}
					autoSelect={autoSelect}
					valueProp={"id"}
					textProp={"value"}
					isMulti={multiSelect}
					useAllDisplay={useAllDisplay}
					fullLength
					placeholder={placeholder}
					loaded={loaded}
				/>
				{childComponents}
			</>
		);
	}

	function getEntrySelector() {
		const renderAsPanels = useMemo(() => type === componentType.panelSelector, [type]);

		const selectorValues = useMemo(() => (preLoadedOptions ?? options)?.data ?? [], [preLoadedOptions, options]);

		const loaded = useMemo(
			() => (hasUrl ? !url || hasValue(options) : (optionsLoaded ?? hasValue(options))),
			[hasUrl, url, options, optionsLoaded],
		);

		const childComponents = useMemo(
			() =>
				components?.map((c) => (
					<DynamicComponent
						{...props}
						component={c}
						key={c.propertyName}
						parentPropertyName={propertyName}
						readOnly={c.readOnly || fullReadOnly}
						fullReadOnly
					/>
				)),
			[props, propertyName, fullReadOnly],
		);

		return (
			<>
				<Selector
					{...defaultProps}
					values={selectorValues}
					id={linkName}
					isMulti={multiSelect}
					renderAsPanels={renderAsPanels}
					readOnly={readOnly}
					loaded={loaded}
					size={size}
					selectIfSingle={selectIfSingle}
					clearable={selectIfSingle}
					changeHandler={dropdownChanged}
				/>
				{childComponents}
			</>
		);
	}

	function getDateSelection() {
		const optionsLoaded = options?.data?.length > 0;
		const datePickerOptions = useMemo(() => {
			if (!optionsLoaded) return {} as DateTimePickerProps;
			return {
				enabledDates: options.values().map((d) => holdInUtc(d)),
			} as DateTimePickerProps;
		}, [options, optionsLoaded]);

		return (
			<DateTimePicker
				{...defaultProps}
				{...datePickerOptions}
				disabled={disabled || readOnly || !optionsLoaded}
				allowInput={false}
				dateOnly
				placeholder={hasValue(inputValue) ? "" : "Select..."}
				changeHandler={componentValueChanged}
			/>
		);
	}

	function getMonthSelection() {
		const optionsLoaded = options?.data?.length > 0;

		const datePickerOptions = useMemo(() => {
			if (!optionsLoaded) return {} as DateTimePickerProps;
			//Month Selection only allows for minDate/maxDate. Using the enabledDates property will cause the plugin to fail.
			const enabledDates = options.values().map((d) => new Date(d));
			return {
				minDate: enabledDates[0],
				maxDate: enabledDates[enabledDates.length - 1],
			} as DateTimePickerProps;
		}, [options, optionsLoaded]);

		const onChange = useCallback(
			async (selectedDates: Date[]) => await componentValueChanged(selectedDates[0], propertyName),
			[propertyName, componentValueChanged],
		);

		return (
			<DateTimePicker
				{...defaultProps}
				{...datePickerOptions}
				disabled={disabled || readOnly || !optionsLoaded}
				allowInput={false}
				placeholder={hasValue(inputValue) ? "" : "Select..."}
				onChange={onChange}
				monthsOnly
			/>
		);
	}

	function getPhotoCapture(isSelfie = false, mobile = false, mobileOnly = false) {
		const onError = (e: string) => PushErrorNotification(e);
		let instructionText = getTemplateValue(props, baseComponentValue);
		if (instructionText === "null") instructionText = undefined;
		return (
			<TakePicture
				{...defaultProps}
				isSelfie={isSelfie}
				isMobile={mobile}
				fullWidth={mobile}
				instructionText={instructionText}
				changeHandler={componentValueChanged}
				onError={onError}
				openAtStart
				mobileOnly={mobileOnly}
			/>
		);
	}

	function getAudioRecording() {
		return <AudioRecording {...defaultProps} changeHandler={componentValueChanged} />;
	}

	function getBlinkId() {
		return <BlinkId {...defaultProps} apiKey={apiKey} changeHandler={componentValueChanged} hideLabel />;
	}

	function getEntryFileUpload() {
		const { values: files, uploadedValues: uploadedFiles } = propertyValue;
		const fileTypes = useMemo(() => baseComponentValue.split(","), [baseComponentValue]);
		return (
			<FileUploader
				label={componentLabel}
				id={linkName}
				onFilesAdded={fileUploadChange}
				multiple={!hasValue(maxLength) || maxLength > 1}
				showFileIcons
				acceptFileTypes={fileTypes}
				errorText={validationError}
				files={files}
				disabled={readOnly}
				uploadedFiles={uploadedFiles}
				getFileFromServer={getUploadedFile}
				removeUploadedFile={deleteUploadedFile}
			/>
		);
	}

	function getDocumentTypeUpload() {
		return (
			<DocumentTypeUpload
				{...defaultProps}
				{...props}
				inputValue={inputValue}
				docTypes={((preLoadedOptions ?? options)?.data ?? []) as any}
				optionsLoaded={optionsLoaded}
				onBlur={onBlur}
				validationResults={validationResults}
				valueChanged={valueChanged}
				valuesChanged={valuesChanged}
				componentValueChanged={componentValueChanged}
			/>
		);
	}

	function getDocumentReview() {
		//store doc review in state of cmp
		//clean up doc review document of body,html, style, etc when putting into state
		if (!optional) return null;
		return (
			<DocumentReview
				{...defaultProps}
				document={(optional as DocumentDefinition)?.document}
				type={(optional as DocumentDefinition)?.fileType}
				hideLabel
				changeHandler={componentValueChanged}
			/>
		);
	}

	function getEntrySignature() {
		return <Signature {...defaultProps} changeHandler={componentValueChanged} />;
	}

	function getEntryDocument() {
		const Loading = useCallback(
			() => (
				<>
					<h2>{componentLabel}</h2>
					{"Loading..."}
				</>
			),
			[componentLabel],
		);

		const documentComponents = useMemo(
			() =>
				components.map((c) => (
					<div key={c.propertyName} className={style.docReviewItem}>
						<DynamicComponent
							{...props}
							component={c}
							parentPropertyName={propertyName}
							readOnly={c.readOnly || fullReadOnly}
							fullReadOnly
						/>
					</div>
				)),
			[components, propertyName, fullReadOnly],
		);

		return (
			<div id={propertyName} data-subsection={propertyName} className={style.documentReview}>
				{!components?.length ? <Loading /> : documentComponents}
			</div>
		);
	}

	function getGoogleMapCapture() {
		return (
			<GoogleMapCapture
				{...defaultProps}
				changeHandler={componentValueChanged}
				addressToCapture={optional}
				locationTracker={locationTracker}
			/>
		);
	}

	function getCurrentLocationFinder() {
		return (
			<CurrentLocationFinder
				propertyName={propertyName}
				locationTracker={locationTracker}
				valueChanged={valueChanged}
			/>
		);
	}

	function getButton() {
		return <DynamicButton mappedComponent={component} />;
	}

	function getTemplate() {
		if (propertyName) {
			if (Array.isArray(propertyValue)) {
				return (
					<>
						{propertyValue.map((value, index) => (
							<div key={index}>
								<Template {...props} value={value} rootProperty={`${propertyName}[${index}].`} />
							</div>
						))}
					</>
				);
			}
		}
		return <Template {...props} />;
	}

	function getBulletedTemplate() {
		const listItems = useMemo(() => {
			return components.map((c, i) => (
				<li key={`${c.propertyName}${i}`}>
					<DynamicComponent {...props} component={c} parentPropertyName={propertyName} />
				</li>
			));
		}, [components, props, propertyName]);
		return <ul className={styles(inline && style.inlineBulletedTemplate)}>{listItems}</ul>;
	}

	function getHtmlTemplate() {
		return <Template {...props} clickable={!componentReadOnly} placeholder={propertyName} html />;
	}

	function getText() {
		return <p className={styles(style.text, componentStyles)}>{baseComponentValue}</p>;
	}

	function getPropertyDisplay() {
		const { label, value, classNames, propertyClassNames } = getPropertyDisplayData();

		return (
			<div className={styles(classNames, inline && style.inlinePropertyDisplay)} id={linkName}>
				<label className={style.controlLabel}>{label}</label>
				<span className={propertyClassNames}>{value}</span>
			</div>
		);
	}

	function getPropertyDateDisplay() {
		const { label, value, classNames, propertyClassNames } = getPropertyDisplayData();
		return (
			<div className={classNames} id={linkName}>
				<label className={style.controlLabel}>{label}</label>
				<span className={styles(style.propertyDateDisplay, propertyClassNames)}>{dateTimeOf(value)}</span>
			</div>
		);
	}

	function getPropertyDateOnlyDisplay() {
		const { label, value, classNames, propertyClassNames } = getPropertyDisplayData();
		return (
			<div className={classNames} id={linkName}>
				<label className={style.controlLabel}>{label}</label>
				<span className={styles(style.propertyDateOnlyDisplay, propertyClassNames)}>{dateOf(value)}</span>
			</div>
		);
	}

	function getPropertyTimeOnlyDisplay() {
		const { label, value, classNames, propertyClassNames } = getPropertyDisplayData();
		return (
			<div className={classNames} id={linkName}>
				<label className={style.controlLabel}>{label}</label>
				<span className={styles(style.propertyDateOnlyDisplay, propertyClassNames)}>{timeOf(value)}</span>
			</div>
		);
	}

	function getPropertyCurrencyDisplay() {
		const { label, value, classNames, propertyClassNames } = getPropertyDisplayData();
		return (
			<div className={classNames} id={linkName}>
				<label className={style.controlLabel}>{label}</label>
				<span className={propertyClassNames}>{currency(value, false)}</span>
			</div>
		);
	}

	function getPropertyPhotoDisplay() {
		const { label, classNames } = getPropertyDisplayData();

		if (defaultProps.value == null) return null;

		return (
			<div className={classNames} id={linkName}>
				<label className={style.controlLabel}>{label}</label>
				<ImageDisplay {...defaultProps} hideLabel style={{ width: 256, maxWidth: 256, marginLeft: 16 }} />
			</div>
		);
	}

	function getTitle() {
		return <h2 className={styles(style.text)}>{baseComponentValue}</h2>;
	}

	function getTablePropertyDisplay() {
		const { value } = getPropertyDisplayData();
		return (
			<TablePropertyDisplay
				id={linkName}
				label={componentLabel}
				value={String(value).includes("this.") ? "" : value}
			/>
		);
	}

	function getTableDateDisplay(dateOnly = false) {
		const { value } = getPropertyDisplayData();
		return <TableDateDisplay id={linkName} label={componentLabel} value={value} dateOnly={dateOnly} />;
	}

	function getDisplayAddressComponent() {
		const address = inputValue as address;
		if (!address) return null;

		return (
			<div id={propertyName} data-subsection={propertyName} className={style.addressDisplay}>
				<b>{componentLabel}</b>
				<p>{address.address1}</p>
				{address.address2 && <p>{address.address2}</p>}
				<p>
					{address.city}, {address.stateName} {address.zip}
				</p>
			</div>
		);
	}

	function getImageDisplay() {
		return <ImageDisplay {...defaultProps} />;
	}

	function getAudioDisplay() {
		return <AudioDisplay {...defaultProps} />;
	}

	//Helper functions
	function getPropertyDisplayData() {
		const label = componentLabel ?? "";
		return {
			label: inline && !label.endsWith("?") ? `${label}:` : label,
			value: propertyValue ?? baseComponentValue,
			classNames: styles(style.displayGroup, inline && style.inline),
			propertyClassNames: styles(inline ? style.inlineValue : ""),
		};
	}
}

function isObjectLike(value: unknown) {
	return value !== null && typeof value === "object";
}

/** Renders the consumer's addon from the public context, so it refreshes on every consumer render as before. */
function ComponentAddon(props: { propertyName: string }) {
	const { getComponentAddon } = useComponentContext();
	return getComponentAddon?.(props.propertyName) ?? null;
}

export default DynamicComponent;
